import React, { useState } from "react";
import { toast } from "sonner";
import { KeyRound, ArrowLeft, Lock, Clapperboard, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TopNav from "@/components/TopNav";
import KeyCard from "@/components/KeyCard";
import OtpDialog, { useOtp } from "@/components/OtpDialog";
import { api, errMsg, getUnlock, setUnlock } from "@/lib/api";

const LockedPanel = ({ onUnlocked }) => {
  const o = useOtp("/settings/unlock/otp", async (otp, code) => {
    try {
      const { data } = await api.post("/settings/unlock", { challenge_id: otp.challenge_id, code });
      setUnlock(data.unlock_token, data.expires_in);
      toast.success("API keys unlocked for 15 minutes");
      onUnlocked();
      return true;
    } catch (e) { toast.error(errMsg(e, "Wrong code")); return false; }
  });
  return (
    <section className="ax-panel p-8 mt-8 flex flex-col items-center text-center gap-4" data-testid="settings-locked">
      <div className="h-14 w-14 rounded-2xl bg-zinc-800 border border-zinc-700 flex items-center justify-center"><Lock className="w-6 h-6 text-zinc-300" /></div>
      <div className="font-heading text-xl font-semibold">API keys are locked</div>
      <p className="text-sm text-zinc-500 max-w-sm">To view or change your API keys, approve with the one-time code sent to tom@blackfx.net.</p>
      <button onClick={() => o.ask("open")} disabled={o.busy} className="ax-generate h-11 px-6" data-testid="settings-unlock-button">
        {o.busy && !o.otp ? <Loader2 className="w-4 h-4 animate-spin" /> : <><KeyRound className="w-4 h-4" />Send code to unlock</>}
      </button>
      <OtpDialog o={o} testid="settings-unlock-otp" title="Unlock API keys" />
    </section>
  );
};

export default function Settings() {
  const nav = useNavigate();
  const [unlocked, setUnlocked] = useState(!!getUnlock());
  const relock = () => { setUnlock(null); setUnlocked(false); toast.error("Unlock expired — verify the code again"); };

  return (
    <div className="min-h-screen relative z-[1] bg-transparent text-zinc-100">
      <TopNav />
      <main className="max-w-2xl mx-auto px-6 py-12">
        <button onClick={() => nav(sessionStorage.getItem("ax_last_studio") || "/projects")} className="text-xs text-zinc-500 hover:text-white flex items-center gap-1 mb-6" data-testid="settings-back"><ArrowLeft className="w-3.5 h-3.5" />Back</button>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Settings</h1>
        {!unlocked ? <LockedPanel onUnlocked={() => setUnlocked(true)} /> : (
          <>
            <p className="text-zinc-500 mt-2 text-sm">Image, video, remix and audio run on your Sume key. The Black FX panel runs on your Black FX (LTX) key.</p>
            <KeyCard provider="sume" title="Sume API key" subtitle="Billing happens on your Sume balance (USD)" icon={KeyRound} iconClass="ax-gradient-bg text-black" onLocked={relock}
              link={{ href: "https://www.sume.com/dashboard/api-keys", label: "Get a key at sume.com → Dashboard → API keys" }}
              extra={(st) => (
                <div className="rounded-lg bg-[#0D0D0F] border border-zinc-800 p-3">
                  <div className="text-zinc-500 font-mono uppercase tracking-widest text-[10px]">Models unlocked</div>
                  <div className="text-zinc-200 mt-1" data-testid="settings-models-count"><b className="text-white">{st.available_models}</b> of {st.total_models} in Ai Xtudio</div>
                </div>
              )} />
            <KeyCard provider="ltx" title="Black FX API Key" subtitle="Powers BFX AlphaGen and BFX Extend · billed on your LTX credits (USD)" icon={Clapperboard} iconClass="bfx-gradient-bg text-white" buttonClass="bfx-generate" onLocked={relock}
              link={{ href: "https://docs.ltx.io/authentication", label: "How to get your key from the LTX developer console" }}
              extra={() => (
                <div className="rounded-lg bg-[#0D0D0F] border border-zinc-800 p-3">
                  <div className="text-zinc-500 font-mono uppercase tracking-widest text-[10px]">Tools unlocked</div>
                  <div className="text-zinc-200 mt-1">BFX AlphaGen · BFX Extend</div>
                </div>
              )} />
            <p className="text-[11px] text-zinc-500 mt-4 flex items-center gap-1.5" data-testid="settings-otp-note"><KeyRound className="w-3 h-3" />Opening, changing or removing keys needs a one-time code sent to tom@blackfx.net.</p>
            <p className="text-[11px] text-zinc-600 mt-2">Keys are stored encrypted and never shown in full. Unlock lasts 15 minutes in this tab.</p>
          </>
        )}
      </main>
    </div>
  );
}
